(() => {
  'use strict';
  if (!window.customElements || customElements.get('fs-chart-dialog')) return;

  const candidates = 'product-info .product__accordion summary, product-info modal-opener[data-modal] button, fs-live-quickview .fs-live-quickview__detail > summary, fs-bundle-product .fs-bundle-product__detail > summary';
  const chartLabel = (value) => /\b(?:size|sizing)\s+(?:chart|guide)\b/i.test(String(value || ''));
  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const safeURL = (value) => {
    try {
      const url = new URL(value || '', document.baseURI || location.href);
      return value && url.protocol === 'https:' ? url.href : '';
    } catch (_) { return ''; }
  };
  const allowedTags = new Set(['P', 'DIV', 'SECTION', 'SPAN', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'BR', 'HR', 'UL', 'OL', 'LI', 'DL', 'DT', 'DD', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'CAPTION', 'FIGURE', 'FIGCAPTION', 'IMG', 'BLOCKQUOTE', 'SUP', 'SUB', 'A']);
  const blockedTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'TEMPLATE', 'NOSCRIPT', 'SVG', 'MATH', 'LINK', 'META']);

  class FsChartDialog extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.connected = true;
      this.content = this.el('content');
      this.scroll = this.el('scroll');
      this.closeButton = this.querySelector('[data-fs-chart-close]');
      this.onDocumentClick = (event) => {
        if (event.defaultPrevented || (typeof event.button === 'number' && event.button !== 0)) return;
        const trigger = event.target?.closest?.(candidates);
        if (!trigger || !chartLabel(trigger.textContent)) return;
        if (!this.open(trigger)) return;
        event.preventDefault();
        // Stop Dawn's original opener only after this dialog has opened successfully.
        event.stopImmediatePropagation();
      };
      this.onClick = (event) => {
        if (event.target.closest('[data-fs-chart-close]')) this.close();
        if (event.target === this.dialog && this.backdropStart && this.outside(event)) this.close();
        this.backdropStart = false;
      };
      this.onPointerDown = (event) => { this.backdropStart = event.target === this.dialog && this.outside(event); };
      this.onPointerCancel = () => { this.backdropStart = false; };
      this.onCancel = (event) => { event.preventDefault(); this.close(); };
      this.onClose = () => this.finishClose();
      this.onKeyDown = (event) => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]), a[href], iframe, [tabindex]:not([tabindex="-1"])'))
          .filter((node) => !node.closest('[hidden]') && node.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const current = this.ownerDocument.activeElement;
        if (!first) { event.preventDefault(); this.closeButton.focus(); return; }
        if (event.shiftKey && (current === first || !this.dialog.contains(current))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (current === last || !this.dialog.contains(current))) { event.preventDefault(); first.focus(); }
      };
      this.onParentClose = () => this.close(false);
      this.onHistory = () => this.close();
      this.ownerDocument.addEventListener('click', this.onDocumentClick, true);
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('pointerdown', this.onPointerDown);
      this.dialog.addEventListener('pointercancel', this.onPointerCancel);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('popstate', this.onHistory);
      this.enhance(this.ownerDocument);
      if (typeof MutationObserver === 'function') {
        this.observer = new MutationObserver((records) => {
          records.forEach((record) => {
            if (this.contains(record.target)) return;
            record.addedNodes.forEach((node) => { if (node.nodeType === 1) this.enhance(node); });
          });
          if (this.dialog.open && this.opener && !this.opener.isConnected) this.close(false);
        });
        this.observer.observe(this.ownerDocument.body, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.close(false);
      this.finishClose();
      this.observer?.disconnect();
      this.ownerDocument.removeEventListener('click', this.onDocumentClick, true);
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('pointerdown', this.onPointerDown);
      this.dialog.removeEventListener('pointercancel', this.onPointerCancel);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('popstate', this.onHistory);
      this.ownerDocument.querySelectorAll('.fs-chart-trigger').forEach((trigger) => this.restoreTrigger(trigger));
      this.connected = false;
    }

    el(name) { return this.querySelector('[data-fs-chart-' + name + ']'); }
    enhance(root) {
      const triggers = Array.from(root.querySelectorAll(candidates));
      if (root.matches?.(candidates)) triggers.unshift(root);
      triggers.forEach((trigger) => {
        if (!chartLabel(trigger.textContent) || !this.sourceFor(trigger) || trigger.classList.contains('fs-chart-trigger')) return;
        trigger._fsChartAttributes = { haspopup: trigger.getAttribute('aria-haspopup'), controls: trigger.getAttribute('aria-controls') };
        trigger.classList.add('fs-chart-trigger');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
      });
    }
    restoreTrigger(trigger) {
      const original = trigger._fsChartAttributes;
      if (!original) return;
      ['haspopup', 'controls'].forEach((key) => {
        if (original[key] === null) trigger.removeAttribute('aria-' + key);
        else trigger.setAttribute('aria-' + key, original[key]);
      });
      trigger.classList.remove('fs-chart-trigger');
      delete trigger._fsChartAttributes;
    }
    sourceFor(trigger) {
      const details = trigger.closest('details');
      if (trigger.tagName === 'SUMMARY' && details) {
        // This content was rendered by the selected product template. No category fallback.
        return Array.from(details.children).find((child) => child.matches('.accordion__content, .fs-live-quickview__rich, .fs-bundle-product__rich')) || null;
      }
      const target = trigger.closest('modal-opener')?.getAttribute('data-modal');
      if (!target || !/^#[A-Za-z][\w:.-]*$/.test(target)) return null;
      return this.ownerDocument.getElementById(target.slice(1))?.querySelector('.product-popup-modal__content-info') || null;
    }
    outside(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }
    open(trigger) {
      if (this.dialog.open) return true;
      const source = this.sourceFor(trigger);
      if (!source) return false;
      const title = cleanText(trigger.textContent);
      const fragment = this.copyChart(source, title);
      this.content.replaceChildren(fragment);
      this.el('title').textContent = title;
      const empty = !cleanText(this.content.textContent) && !this.content.querySelector('img');
      this.el('empty').textContent = this.dialog.dataset.emptyLabel;
      this.el('empty').hidden = !empty;
      this.content.hidden = empty;
      this.setProduct(trigger);
      this.opener = trigger;
      this.parentDialog = trigger.closest('dialog');
      this.returnFocus = true;
      try { this.dialog.showModal(); } catch (_) {
        this.content.replaceChildren();
        this.opener = null;
        this.parentDialog = null;
        return false;
      }
      this.scrollState = { body: this.ownerDocument.body.style.overflow, html: this.ownerDocument.documentElement.style.overflow };
      this.ownerDocument.body.style.overflow = 'hidden';
      this.ownerDocument.documentElement.style.overflow = 'hidden';
      this.parentDialog?.addEventListener('close', this.onParentClose);
      this.scroll.scrollTop = 0;
      this.querySelector('fs-fit-wizard')?.openFor(trigger);
      this.closeButton.focus({ preventScroll: true });
      return true;
    }
    setProduct(trigger) {
      const quickview = trigger.closest('fs-live-quickview, fs-bundle-product');
      const product = trigger.closest('product-info');
      const title = quickview?.querySelector('[data-fs-product-title]') || product?.querySelector('.product__title h1') || product?.querySelector('.product__title h2') || product?.querySelector('.product__title');
      const image = quickview?.querySelector('[data-fs-product-image]') || product?.querySelector('media-gallery .product__media img, media-gallery img');
      const src = image ? safeURL(image.currentSrc || image.getAttribute('src')) : '';
      this.el('product-title').textContent = cleanText(title?.textContent);
      this.el('product').hidden = !cleanText(title?.textContent);
      const photo = this.el('photo');
      photo.hidden = !src;
      photo.onerror = () => { photo.hidden = true; };
      if (src) photo.src = src;
      else photo.removeAttribute('src');
    }
    close(restoreFocus = true) {
      if (!this.dialog?.open) return;
      this.returnFocus = restoreFocus;
      this.dialog.close();
    }
    finishClose() {
      this.querySelector('fs-fit-wizard')?.close();
      const opener = this.opener;
      this.parentDialog?.removeEventListener('close', this.onParentClose);
      // When the underlying quickview closes first, its own handler has already
      // restored the page. Do not reinstate that quickview's obsolete scroll lock.
      if (this.scrollState && (!this.parentDialog || this.parentDialog.open)) {
        this.ownerDocument.body.style.overflow = this.scrollState.body;
        this.ownerDocument.documentElement.style.overflow = this.scrollState.html;
      }
      this.scrollState = null;
      this.parentDialog = null;
      this.opener = null;
      this.backdropStart = false;
      this.content?.replaceChildren();
      if (this.returnFocus && opener?.isConnected) opener.focus({ preventScroll: true });
      this.returnFocus = false;
    }
    copyChart(source, title) {
      const doc = this.ownerDocument;
      const fragment = doc.createDocumentFragment();
      const firstHeading = source.matches('.product-popup-modal__content-info') ? source.querySelector('h1, h2, h3') : null;
      const copy = (node, parent) => {
        if (node.nodeType === 3) { parent.append(doc.createTextNode(node.textContent)); return; }
        if (node.nodeType !== 1 || blockedTags.has(node.tagName)) return;
        if (node === firstHeading && cleanText(node.textContent).toLowerCase() === title.toLowerCase()) return;
        if (!allowedTags.has(node.tagName)) { Array.from(node.childNodes).forEach((child) => copy(child, parent)); return; }
        const tag = /^H[1-6]$/.test(node.tagName) ? 'h3' : node.tagName.toLowerCase();
        const element = doc.createElement(tag);
        if (tag === 'img') {
          const src = safeURL(node.currentSrc || node.getAttribute('src') || node.getAttribute('data-src'));
          if (!src) return;
          element.src = src;
          element.alt = node.getAttribute('alt') || '';
          element.loading = 'eager';
          element.decoding = 'async';
          element.addEventListener('error', () => {
            if (!element.isConnected || element.hidden) return;
            element.hidden = true;
            const error = doc.createElement('p');
            error.className = 'fs-chart-dialog__image-error';
            error.textContent = this.dialog.dataset.imageErrorLabel;
            element.after(error);
          }, { once: true });
        }
        if (tag === 'a') {
          const href = safeURL(node.getAttribute('href'));
          if (href) { element.href = href; element.target = '_blank'; element.rel = 'noopener noreferrer'; }
        }
        if (tag === 'td' || tag === 'th') {
          ['colspan', 'rowspan'].forEach((name) => {
            const value = node.getAttribute(name);
            if (/^[1-9]\d?$/.test(value || '')) element.setAttribute(name, value);
          });
          const scope = node.getAttribute('scope');
          if (['col', 'row', 'colgroup', 'rowgroup'].includes(scope)) element.setAttribute('scope', scope);
        }
        Array.from(node.childNodes).forEach((child) => copy(child, element));
        if (tag === 'table') {
          const scroll = doc.createElement('div');
          scroll.className = 'fs-chart-dialog__table-scroll';
          scroll.tabIndex = 0;
          scroll.setAttribute('role', 'region');
          scroll.setAttribute('aria-label', title);
          scroll.append(element);
          parent.append(scroll);
        } else parent.append(element);
      };
      Array.from(source.childNodes).forEach((node) => copy(node, fragment));
      return fragment;
    }
  }
  customElements.define('fs-chart-dialog', FsChartDialog);
})();
